import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import logo from '../../Assets/Images/logo.svg';

import useAuth from 'Hooks/useAuth';
import DrawerNavigation from 'Components/DrawerNavigation';
import Alert from 'Components/Dialog/Alert';
import Routes from './Routes';
import UserManager from './UserManager';
import CompanyManager from './CompanyManager';
import OfferManager from './OfferManager';
import InventoryManager from './InventoryManager';
import DeviceToken from '../DeviceToken';
import Config from '../../config';
import OfferNotification from '../../Components/Notifications/Offer';
import NewsNotification from '../../Components/Notifications/News';
import OrderHistoryNotification from '../../Components/Notifications/OrderHistory';
import UpdateAuthTokenFromOld from '../../Components/UpdateAuthTokenFromOld';
import useRedirectPath from 'Hooks/useRedirectPath';
import Synchronizers from 'Components/Synchronizers';
import ResetURIToHome from 'Components/ResetURIToHome';
import { useDispatch, useSelector } from 'Hooks';

import * as actions from 'actions';
import { useStorage } from 'Components/Storage';
import CookieBanner from 'Components/CookieBanner';
import { Language } from 'Types';
import CartManager from './CartManager';

const Container = styled.div<{ paddingLeft: number }>`
  min-height: var(--height);
  display: flex;
  flex-direction: column;

  @media (min-width: 768px) {
    margin: 0 0 0 ${({ paddingLeft }) => `${paddingLeft}px`};
    transform: translate(0px, 0px);
  }
`;

function App() {
  const { hasAuthToken } = useAuth();
  const isSignedIn = hasAuthToken();
  const location = useLocation();
  const { pathname } = useLocation();
  const [storage, updateStorage] = useStorage();
  const cartId = useSelector((state) => state.cartId);
  const defaultLanguage = useSelector((state) => state.defaultLanguage);
  const dispatch = useDispatch();

  const onResize = () => {
    if (window.innerWidth >= 768) {
      dispatch(actions.toggleMainMenu(true));
    }
  };

  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // saving redirect path after authorization to store
  useRedirectPath();

  /**
   * Initialize google analytics
   */
  useEffect(() => {
    if (storage.analyticsConsent == false) {
      // @ts-ignore: Cannot index window using string
      window[`ga-disable-${Config.ANALYTICS_ID}`] = true;
    } else {
      ReactGA.initialize(Config.ANALYTICS_ID, {
        gtagOptions: {
          analytics_storage: 'denied',
        },
        gaOptions: {
          anonymizeIp: true,
        },
      });
    }
  }, [storage.analyticsConsent]);

  /**
   * On mount, set cart ID if we have one in storage.
   * This makes page reloads retain state
   */
  useEffect(() => {
    if (storage.cartId && (Config.ANONYMOUS_PURCHASE || pathname.includes('/order'))) {
      dispatch(actions.setCartId(storage.cartId));
    }
    if (storage.redirectPath && Config.ANONYMOUS_PURCHASE) {
      dispatch(actions.setRedirectPath(storage.redirectPath));
    }
  }, [storage.cartId]);

  /**
   * On mount, set language to browser language or company default language.
   */
  useEffect(() => {
    const browserLang = window.navigator.language.slice(0, 2);
    const isSupportedLanguage =
      browserLang === 'se' || browserLang === 'en' || browserLang === 'es';
    if (isSupportedLanguage) {
      dispatch(actions.changeLanguage(browserLang as Language));
    } else {
      dispatch(actions.changeLanguage(defaultLanguage));
    }
  }, []);

  /**
   * Update local storage when cart ID changes
   */
  useEffect(() => {
    if (!cartId || cartId === storage.cartId) {
      return;
    }
    updateStorage({
      cartId,
    });
  }, [cartId]);

  useEffect(() => {
    // Wait a few ms with tracking page until title has changed
    setTimeout(() => {
      ReactGA.send({ hitType: 'pageview', page: location.pathname, title: document.title });
    }, 200);
  }, [location]);

  // Check if cookie banner should show
  // Don't show in app-mode (hasNativeWrapper)
  //const showCookieBanner = !window.hasNativeWrapper && storage.cookieConsent == null;

  return (
    <>
      <Helmet>
        <title>{Config.DISPLAY_NAME}</title>
        <link rel="icon" href={logo} sizes="32x32" />
      </Helmet>
      <CartManager />
      <UserManager />
      <UpdateAuthTokenFromOld />
      <CompanyManager />
      <OfferManager />
      <InventoryManager />
      <DrawerNavigation isSignedIn={isSignedIn} />
      <Container paddingLeft={window.sidebarWidth}>
        <Routes />
        <Alert />
      </Container>
      <DeviceToken />
      <OfferNotification />
      <NewsNotification />
      <OrderHistoryNotification />
      <Synchronizers />
      <ToastContainer />
      <ResetURIToHome />
      {/* showCookieBanner && <CookieBanner /> */}
    </>
  );
}

export default App;
